import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Shield, 
  Lock, 
  Eye, 
  FileCheck, 
  Server, 
  Key,
  Check,
  AlertTriangle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

export default function Security() {
  const navigate = useNavigate();

  const securityFeatures = [
    {
      icon: Lock,
      title: "End-to-End Encryption",
      description: "All meetings use AES-256 encryption. Your data is encrypted before leaving your device and can only be decrypted by meeting participants."
    },
    {
      icon: Key,
      title: "Two-Factor Authentication",
      description: "Add an extra layer of security to your account with 2FA support via authenticator apps and SMS."
    },
    {
      icon: Shield,
      title: "Password-Protected Meetings",
      description: "Secure your meetings with passwords and control who can join with waiting rooms and host approval."
    },
    {
      icon: Server,
      title: "Secure Infrastructure",
      description: "Hosted on enterprise-grade cloud infrastructure with automatic backups and 99.9% uptime SLA."
    },
    {
      icon: Eye,
      title: "Privacy by Design",
      description: "We don't sell your data. Period. Your information is used solely to provide our services."
    },
    {
      icon: FileCheck,
      title: "Regular Security Audits",
      description: "Third-party penetration testing and security audits ensure our platform stays secure."
    }
  ];

  const certifications = [
    "SOC 2 Type II Certified",
    "GDPR Compliant",
    "CCPA Compliant",
    "ISO 27001 Certified",
    "HIPAA Compliant (Enterprise)",
    "PCI DSS Level 1"
  ];

  const dataProtection = [
    {
      title: "Data Encryption at Rest",
      description: "All stored data is encrypted using AES-256 encryption standard."
    },
    {
      title: "Data Encryption in Transit",
      description: "All data transferred uses TLS 1.3 encryption protocol."
    },
    {
      title: "Data Residency",
      description: "Choose where your data is stored with regional data centers."
    },
    {
      title: "Automatic Data Deletion",
      description: "Meeting data is automatically deleted based on your retention settings."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24">

        <div className="max-w-6xl mx-auto space-y-12">
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-12 w-12 text-primary mr-3" />
              <h1 className="text-4xl font-bold">Security & Privacy</h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Your trust is our priority. We implement industry-leading security measures to protect your communications.
            </p>
          </div>

          {/* Security Features */}
          <section>
            <h2 className="text-3xl font-bold text-center mb-8">Security Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {securityFeatures.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <feature.icon className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Certifications */}
          <section>
            <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Compliance & Certifications</CardTitle>
                <CardDescription>Meeting global security and privacy standards</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-muted-foreground font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Data Protection */}
          <section>
            <h2 className="text-3xl font-bold text-center mb-8">Data Protection</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {dataProtection.map((item, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Lock className="h-5 w-5 text-primary" />
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Privacy Commitment */}
          <section>
            <Card className="border-primary">
              <CardHeader className="text-center">
                <Eye className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle className="text-2xl">Our Privacy Commitment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-w-3xl mx-auto">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>We don't sell your data.</strong> Your information is never sold to third parties or used for advertising.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>You own your data.</strong> You can export or delete your data at any time.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>Transparent practices.</strong> Our privacy policy clearly explains how we handle your data.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      <strong>Minimal data collection.</strong> We only collect what's necessary to provide our services.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Responsible Disclosure */}
          <section>
            <Card>
              <CardHeader>
                <AlertTriangle className="h-8 w-8 text-primary mb-2" />
                <CardTitle className="text-xl">Responsible Disclosure</CardTitle>
                <CardDescription>Found a security issue? We want to hear from you.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  We take security seriously and appreciate the security research community's efforts. If you discover a security vulnerability, please email us at:
                </p>
                <div className="bg-muted p-4 rounded-lg">
                  <code className="text-sm">security@meethub.com</code>
                </div>
                <p className="text-sm text-muted-foreground">
                  Please include details about the vulnerability and steps to reproduce it. We'll respond within 48 hours.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* CTA */}
          <section className="text-center space-y-6 py-8">
            <h2 className="text-3xl font-bold">Questions About Security?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our security team is here to help answer any questions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => navigate("/contact")}>
                Contact Security Team
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/privacy")}>
                Read Privacy Policy
              </Button>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
